import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMeQuery } from '@platform/state/api';
import { Lookup, Picker, type IPickerOption } from '@platform/ui';
import type { IUserRef } from '@platform/core/types';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { routes } from '@platform/core/lib/routes';
import type { IControlProps } from '../IControlProps';
import { LookupFooter } from '../lookup/LookupFooter';
import { PeopleSearchDialog } from '../lookup/PeopleSearchDialog';
import { memberOptions } from '../options/memberOptions';
import { useWorkspaceMembers } from '../options/useWorkspaceMembers';
import { PersonLookupView } from './PersonLookupView';

const SHOWN = 8;

/**
 * Особа (UIUX/field-types/person, controls/lookup; прототип fieldVM user): комбобокс із аватаром 22px і ім'ям, пошук учасників
 * воркспейсу (у порядку членства, поточний користувач першим) за ім'ям або поштою з першого символу, «Не призначено» першим, до 8 збігів
 * з поштою, підвал «N з M», «Учасники ↗» (сторінка
 * учасників робочого простору оболонки, /members) і «Розширений пошук…» (діалог зі списком учасників). У ключовому рядку — пікер «ВИКОНАВЕЦЬ: ім'я ▾»
 * з «(ви)» біля поточного користувача, як у прототипі; у формі й редагуванні в рядку — лише ім'я (прототип: uname без «(you)»). У формі
 * для перегляду — PersonLookupView (прототип: рамка, × і шеврон лишаються, сіре лише поле введення). У міні-формі (compact; прототип qr) —
 * простий список «Не призначено» й імен учасників у вигляді нативного select.
 * Значення — IUserRef з API або ідентифікатор (чернетка); зміна віддає IUserRef (або null).
 */
export function UserLookup({ id, value, onChange, readOnly, field, rt, ws, size, prefix, invalid, placeholder, autoFocus, compact }: IControlProps<IUserRef | string>) {
  const { t } = useTranslation();
  const strings = useModuleStrings();
  const members = useWorkspaceMembers(ws);
  const { data: me } = useMeQuery();
  const [query, setQuery] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const currentId = typeof value === 'string' ? value : value?.id ?? '';
  const current = (typeof value === 'object' && value) || members.find((m) => m.user.id === currentId)?.user || null;
  const nameOf = (u: IUserRef) => (u.id === me?.id ? t('fieldControls.you', { name: u.name }) : u.name);
  const label = strings.field(rt.key, field.key, field.label);
  const pick = (uid: string) => onChange(uid ? members.find((m) => m.user.id === uid)?.user ?? null : null);
  // Як у прототипі: учасники в порядку членства (поточний користувач першим), запит лише відбирає збіги.
  const hits = useMemo(() => memberOptions(members, query), [members, query]);
  const unassigned: IPickerOption = { value: '', label: t('fieldControls.unassigned'), sub: t('fieldControls.clearValue'), weight: 600 };
  if (size === 'key') {
    const options = [{ value: '', label: t('fieldControls.unassigned'), muted: true }, ...hits.map((m) => ({ value: m.user.id, label: nameOf(m.user) }))];
    return <Picker id={id} value={currentId} options={options} onChange={pick} label={label} prefix={prefix} size="key" readOnly={readOnly}
      invalid={invalid} placeholder={t('fieldControls.unassigned')} selected={current ? { value: current.id, label: nameOf(current) } : null} />;
  }
  if (compact) {
    const plain = [{ value: '', label: t('fieldControls.unassigned') }, ...members.map((m) => ({ value: m.user.id, label: m.user.name }))];
    return <Picker id={id} value={currentId} options={plain} onChange={pick} label={label} readOnly={readOnly} invalid={invalid} className="fc-userpick"
      placeholder={t('fieldControls.unassigned')} selected={current ? { value: current.id, label: current.name } : null} />;
  }
  if (readOnly && size !== 'inline') {
    return <PersonLookupView id={id} label={label} text={current?.name ?? ''} initials={current?.initials} placeholder={placeholder ?? t('fieldControls.typeName')} />;
  }
  const options: IPickerOption[] = [unassigned, ...hits.slice(0, SHOWN).map((m) => ({ value: m.user.id, label: m.user.name, sub: m.email ?? undefined, weight: 600 as const }))];
  return (
    <>
      <Lookup id={id} label={label} value={currentId} text={current?.name ?? ''} initials={current?.initials} options={options}
        onQuery={setQuery} onPick={pick} size={size} prefix={prefix} readOnly={readOnly} invalid={invalid} autoFocus={autoFocus}
        placeholder={placeholder ?? t('fieldControls.typeName')}
        footer={<LookupFooter count={hits.length} total={members.length} onAdvanced={() => setAdvanced(true)}
          extra={<Link className="pk-list__link" to={`${routes.home()}/members`} onMouseDown={(e) => e.preventDefault()}>{t('viewsKit.members')}</Link>} />} />
      {advanced && <PeopleSearchDialog ws={ws} members={members} onPick={(uid) => { pick(uid); setAdvanced(false); }} onClose={() => setAdvanced(false)} />}
    </>
  );
}
